import React from 'react';
import { Brain, Search, Cog, FileText, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';

interface ReasoningPipelineMessageProps {
  content: string;
  metadata?: {
    stage: 'analyzing' | 'planning' | 'executing' | 'formatting' | 'processing';
    confidence?: number;
  };
}

const stageConfig = {
  analyzing: {
    icon: Search,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    label: 'Analyzing'
  },
  planning: {
    icon: Brain,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    label: 'Planning'
  },
  executing: {
    icon: Cog,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    label: 'Executing'
  },
  formatting: {
    icon: FileText,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    label: 'Formatting'
  },
  processing: {
    icon: CheckCircle,
    color: 'text-gray-600',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200',
    label: 'Processing'
  }
};

export const ReasoningPipelineMessage: React.FC<ReasoningPipelineMessageProps> = ({
  content,
  metadata
}) => {
  const stage = metadata?.stage || 'processing';
  const config = stageConfig[stage];
  const Icon = config.icon;
  const confidence = metadata?.confidence || Math.floor(Math.random() * 15 + 85); // Mock confidence 85-100%

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 10 }}
      transition={{ duration: 0.3 }}
      className="mb-3"
    >
      <div className={`
        inline-flex items-center gap-3 px-4 py-3 rounded-xl border-l-4 
        ${config.bgColor} ${config.borderColor}
        shadow-sm max-w-md
      `}>
        {/* Animated Icon */}
        <motion.div
          animate={{ 
            rotate: stage === 'executing' ? 360 : 0,
            scale: [1, 1.1, 1]
          }}
          transition={{ 
            rotate: { duration: 2, repeat: Infinity, ease: "linear" },
            scale: { duration: 1.5, repeat: Infinity, ease: "easeInOut" }
          }}
          className={`shrink-0 ${config.color}`}
        >
          <Icon className="h-5 w-5" />
        </motion.div>

        {/* Content and Metadata */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-medium text-gray-700">
              {content}
            </span>
            {/* Confidence Badge */}
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
              {confidence}% confident
            </span>
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${confidence}%` }}
              transition={{ duration: 1, ease: "easeOut" }}
              className={`h-full rounded-full ${
                confidence >= 95 ? 'bg-green-500' :
                confidence >= 85 ? 'bg-blue-500' :
                confidence >= 70 ? 'bg-yellow-500' : 'bg-red-500'
              }`}
            />
          </div>
          
          {/* Safety Indicator */}
          <div className="flex items-center gap-1 mt-1">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <span className="text-xs text-gray-500">Safety guardrails active</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};